import type { Auth, User } from "firebase/auth";
import { signOut } from "./signOut";
import { type FormEvent, useEffect, useRef, useState } from "react";
import * as api from "./api";
import { dayText } from "./accounts";
import { callDay, emailName } from "./calls";
import { ACCOUNT_PAYOUT_COLUMNS, PAYOUT_LINE_COLUMNS, RUN_COLUMNS } from "./columns";
import { rebateText } from "./deals";
import { go, useCrumb, useTitle } from "./hooks";
import { todayIn } from "./pickup";
import {
  adjustmentOf,
  count,
  dollars,
  download,
  HOLD_MAX,
  lastQuarter,
  lineChangeable,
  minimumOf,
  PAYMENT_TEXT,
  PERIOD_COUNT,
  PERIOD_TEXT,
  type PeriodType,
  payingRun,
  periodDates,
  periodTag,
  RUN_STATUS_TEXT,
  runProblem,
  runTotals,
} from "./rebates";
import { type Saved, SERVER_PROBLEM, saveFailure } from "./save";
import { sortRows } from "./sort";
import { SortSelect, SortTh, useSort } from "./SortHeader";
import { Skeleton } from "./Skeleton";

type Failure = "hidden" | "server" | "network" | "session";

const failureOf = (e: unknown): Failure => {
  const code = e instanceof api.ApiError ? e.status : 0;
  return code === 404 || code === 422 ? "hidden" : code === 401 ? "session" : code >= 500 ? "server" : "network";
};

function Problem({ found, auth, retry }: { found: Exclude<Failure, "hidden">; auth: Auth; retry: () => void }) {
  return (
    <p role="alert" className="error">
      {found === "session" ? (
        <>
          Your session ended.{" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </>
      ) : (
        <>
          {found === "server" ? SERVER_PROBLEM : "Cannot reach the server. Check your connection."}{" "}
          <button type="button" onClick={retry}>
            Try again
          </button>
        </>
      )}
    </p>
  );
}

function Status({ saved, auth, testId }: { saved: Saved | null; auth: Auth; testId: string }) {
  return (
    <p role="status" data-testid={testId} className={saved && !saved.ok ? "error" : undefined}>
      {saved?.text}
      {saved && !saved.ok && saved.session && (
        <>
          {" "}
          <button type="button" onClick={() => signOut(auth)}>
            Sign in again
          </button>
        </>
      )}
    </p>
  );
}

const periodDays = (run: api.PayoutRun) => `${dayText(run.starts_on, true)} to ${dayText(run.ends_on, true)}`;

/** Design 9.8: the rebate check runs. Only the roles with the payout grant see this page. */
export function RebateList({ base, org, auth, user, canView }: { base: string; org: api.Bootstrap; auth: Auth; user: User; canView: boolean | undefined }) {
  const [found, setFound] = useState<api.PayoutList | Failure | undefined>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [adding, setAdding] = useState(false);
  const [sort, setSort] = useSort("sort", RUN_COLUMNS);
  useTitle(`Rebates · ${org.name}`);
  useEffect(() => {
    if (canView !== true) return;
    let live = true;
    user
      .getIdToken()
      .then(api.payouts)
      .then((list) => live && setFound(list))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, attempt, canView]);
  if (canView === false || found === "hidden") {
    return (
      <main data-testid="rebates-hidden">
        <h1 tabIndex={-1}>Rebates</h1>
        <p>Your role does not show the rebate runs.</p>
      </main>
    );
  }
  const list = typeof found === "object" ? found : null;
  return (
    <main className="rebates-page wide" data-testid="rebates-page">
      <h1 tabIndex={-1}>Rebates</h1>
      <p className="sub">Each run pays the net gallons of a period at the rebate of the contract that was valid on each pickup day.</p>
      {list?.can_add && !adding && (
        <div className="tabs">
          <button type="button" className="primary" data-testid="run-add" onClick={() => setAdding(true)}>
            Make a run
          </button>
        </div>
      )}
      {list?.can_add && adding && <RunForm base={base} list={list} user={user} onCancel={() => setAdding(false)} auth={auth} />}
      {list && <MinimumFact list={list} auth={auth} user={user} onSaved={(minimum) => setFound({ ...list, minimum })} />}
      {found === undefined ? (
        <Skeleton shape="table" columns={5} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : found.runs.length === 0 ? (
        <p data-testid="rebates-none">No rebate run yet.</p>
      ) : (
        <>
          <SortSelect id="rebates-select" columns={RUN_COLUMNS} sort={sort} onSort={setSort} fallback="Newest period first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-label="Rebate runs">
            <table className="list" data-testid="run-table" data-cols="rebates-select">
              <thead>
                <tr>
                  {RUN_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(found.runs, RUN_COLUMNS, sort).map((run) => (
                  <tr key={run.id} data-testid="run-row" data-tag={run.tag}>
                    <th scope="row">
                      <a href={`${base}/rebates/${run.id}`} onClick={go} data-testid="run-link">
                        {run.tag}
                      </a>
                    </th>
                    <td data-label="Period" title={periodDays(run)}>
                      {run.period}
                      <span className="sub desk-hide"> · {periodDays(run)}</span>
                    </td>
                    <td data-label="Status">{RUN_STATUS_TEXT[run.status]}</td>
                    <td className="num" data-label="Lines">{run.lines}</td>
                    <td className="num" data-label="Held">{run.held || "–"}</td>
                    <td className="num" data-label="To pay">{dollars(run.total)}</td>
                    <td data-label="Made by" title={run.created_email ?? undefined}>
                      {run.created_email ? emailName(run.created_email) : "–"}
                    </td>
                    <td data-label="Approved by" title={run.approved_email ?? undefined}>
                      {run.approved_email ? emailName(run.approved_email) : "–"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </main>
  );
}

function MinimumFact({ list, auth, user, onSaved }: { list: api.PayoutList; auth: Auth; user: User; onSaved: (minimum: number) => void }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(String(list.minimum));
  const [saved, setSaved] = useState<Saved | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const minimum = minimumOf(value);
    if (typeof minimum === "string") return setSaved({ ok: false, text: minimum });
    try {
      const next = await api.setPayoutMinimum(await user.getIdToken(), minimum);
      onSaved(next.minimum);
      setEditing(false);
      setSaved({ ok: true, text: `Saved: the minimum check is ${dollars(next.minimum)}.` });
    } catch (e) {
      setSaved(saveFailure(e, "The minimum"));
    }
  };
  return (
    <>
      <p data-testid="payout-minimum">
        A check below {dollars(list.minimum)} waits for a later run.{" "}
        {list.can_set_minimum && !editing && (
          <button type="button" data-testid="minimum-change" onClick={() => setEditing(true)}>
            Change the minimum
          </button>
        )}
      </p>
      {editing && (
        <form onSubmit={submit} noValidate className="inline-form" data-testid="minimum-form">
          <label htmlFor="payout-minimum-value">Minimum check ($)</label>
          <input id="payout-minimum-value" inputMode="decimal" value={value} onChange={(e) => setValue(e.target.value)} />
          <button type="submit" className="primary">
            Save
          </button>
          <button type="button" onClick={() => setEditing(false)}>
            Cancel
          </button>
        </form>
      )}
      <Status saved={saved} auth={auth} testId="minimum-status" />
    </>
  );
}

/** The minimum check under Settings, for the roles that see the rebate runs (rule Q-27). */
export function PayoutSettings({ auth, user }: { auth: Auth; user: User }) {
  const [list, setList] = useState<api.PayoutList | "failed" | undefined>(undefined);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then(api.payouts)
      .then((next) => live && setList(next))
      .catch(() => live && setList("failed"));
    return () => {
      live = false;
    };
  }, [user]);
  if (list === undefined) return null;
  return (
    <fieldset data-testid="payout-settings">
      <legend>Rebate checks</legend>
      {list === "failed" ? (
        <p className="error">Cannot load the rebate rules. Try again later.</p>
      ) : (
        <MinimumFact list={list} auth={auth} user={user} onSaved={(minimum) => setList({ ...list, minimum })} />
      )}
    </fieldset>
  );
}

function RunForm({ base, list, user, auth, onCancel }: { base: string; list: api.PayoutList; user: User; auth: Auth; onCancel: () => void }) {
  const last = lastQuarter(list.today);
  const [periodType, setPeriodType] = useState<PeriodType>("quarterly");
  const [year, setYear] = useState(String(last.year));
  const [number, setNumber] = useState(String(last.number));
  const [startsOn, setStartsOn] = useState("");
  const [endsOn, setEndsOn] = useState("");
  const [typedTag, setTypedTag] = useState<string | null>(null);
  const [marketValue, setMarketValue] = useState("");
  const [saved, setSaved] = useState<Saved | null>(null);
  const [saving, setSaving] = useState(false);
  const entryId = useRef<string | null>(null);
  const edit = (next: () => void) => {
    next();
    entryId.current = null;
    setSaved(null);
  };
  const tag = typedTag ?? periodTag(periodType, year, number, startsOn, endsOn);
  const dates = periodDates(periodType, Number(year), Number(number), startsOn, endsOn);
  const paying = dates ? payingRun(list.runs, dates.start, dates.end) : null;
  const overlap = paying && `The run "${paying.tag}" (${periodDays(paying)}) pays some of these days already. Select days that it does not pay.`;
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const problem = runProblem({ tag, periodType, year, number, startsOn, endsOn, marketValue, today: list.today }) ?? overlap;
    if (problem) return setSaved({ ok: false, text: problem });
    entryId.current ??= crypto.randomUUID();
    const custom = periodType === "custom";
    const entry: api.RunEntry = {
      id: entryId.current,
      tag: tag.trim(),
      period_type: periodType,
      ...(custom ? { starts_on: startsOn, ends_on: endsOn } : { year: Number(year), number: Number(number) }),
      market_value: marketValue.trim() ? Number(marketValue) : null,
    };
    setSaving(true);
    setSaved(null);
    try {
      const made = await api.makePayoutRun(await user.getIdToken(), entry);
      history.pushState(null, "", `${base}/rebates/${made.run.id}`);
      dispatchEvent(new PopStateEvent("popstate"));
    } catch (e) {
      setSaved(saveFailure(e, "The run"));
    } finally {
      setSaving(false);
    }
  };
  return (
    <form onSubmit={submit} noValidate data-testid="run-form" aria-labelledby="run-form-title">
      <h2 id="run-form-title">Make a draft run</h2>
      <p className="sub">The run reads the pickups and the contracts now. A different person approves it before the export.</p>
      <label htmlFor="run-period-type">Period</label>
      <select id="run-period-type" data-testid="run-period-type" value={periodType} onChange={(e) => edit(() => setPeriodType(e.target.value as PeriodType))}>
        {Object.entries(PERIOD_TEXT).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      {periodType === "custom" ? (
        <>
          <label htmlFor="run-starts">First day</label>
          <input id="run-starts" data-testid="run-starts" type="date" max={list.today} value={startsOn} onChange={(e) => edit(() => setStartsOn(e.target.value))} />
          <label htmlFor="run-ends">Last day</label>
          <input id="run-ends" data-testid="run-ends" type="date" max={list.today} value={endsOn} onChange={(e) => edit(() => setEndsOn(e.target.value))} />
        </>
      ) : (
        <>
          <label htmlFor="run-year">Year</label>
          <input id="run-year" data-testid="run-year" inputMode="numeric" value={year} onChange={(e) => edit(() => setYear(e.target.value))} />
          <label htmlFor="run-number">
            {PERIOD_TEXT[periodType]} (1 to {PERIOD_COUNT[periodType]})
          </label>
          <input id="run-number" data-testid="run-number" inputMode="numeric" value={number} onChange={(e) => edit(() => setNumber(e.target.value))} />
        </>
      )}
      <label htmlFor="run-tag">Name of the run</label>
      <input id="run-tag" data-testid="run-tag" value={tag} onChange={(e) => edit(() => setTypedTag(e.target.value))} />
      <label htmlFor="run-market">Market value ($ per gallon, for a percent rebate)</label>
      <input id="run-market" data-testid="run-market" inputMode="decimal" value={marketValue} onChange={(e) => edit(() => setMarketValue(e.target.value))} />
      {overlap && (
        <p className="error" data-testid="run-overlap">
          {overlap}
        </p>
      )}
      <div className="tabs">
        <button type="submit" className="primary" data-testid="run-save" disabled={saving}>
          {saving ? "Calculating…" : "Make the draft run"}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
      <Status saved={saved} auth={auth} testId="run-status" />
    </form>
  );
}

type RunLoad = api.PayoutDetail | Failure | undefined;

export function RebateRun({ id, base, org, auth, user, listAllowed = true }: { id: string; base: string; org: api.Bootstrap; auth: Auth; user: User; listAllowed?: boolean }) {
  const [found, setFound] = useState<RunLoad>(undefined);
  const [attempt, setAttempt] = useState(0);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [busy, setBusy] = useState(false);
  const [sort, setSort] = useSort("lsort", PAYOUT_LINE_COLUMNS);
  const detail = typeof found === "object" ? found : null;
  useTitle(`${detail?.run.tag ?? "Rebate run"} · ${org.name}`);
  useCrumb(detail ? detail.run.tag : null);
  useEffect(() => {
    let live = true;
    user
      .getIdToken()
      .then((token) => api.payoutRun(token, id))
      .then((next) => live && setFound(next))
      .catch((e) => live && setFound(failureOf(e)));
    return () => {
      live = false;
    };
  }, [user, id, attempt]);
  if (found === "hidden") {
    return (
      <main data-testid="run-missing">
        <h1 tabIndex={-1}>Rebate run</h1>
        <p>
          This run does not exist, or your role does not show it.{" "}
          <a href={listAllowed ? `${base}/rebates` : base} onClick={go}>
            {listAllowed ? "Go to the rebates" : "Go to your home page"}
          </a>
        </p>
      </main>
    );
  }
  const act = async (work: (token: string) => Promise<api.PayoutDetail>, done: string, thing: string) => {
    setBusy(true);
    setSaved(null);
    try {
      setFound(await work(await user.getIdToken()));
      setSaved({ ok: true, text: done });
    } catch (e) {
      setSaved(saveFailure(e, thing));
    } finally {
      setBusy(false);
    }
  };
  const exportRun = (kind: "quickbooks" | "checks") =>
    act(
      async (token) => {
        const file = await api.exportPayoutRun(token, id, kind);
        download(file.file_name, file.csv);
        return file.detail;
      },
      kind === "quickbooks" ? "Saved: the file for QuickBooks." : "Saved: the file for the check merge.",
      "The export",
    );
  return (
    <main className="rebate-run wide" data-testid="run-page" data-status={detail?.run.status}>
      <h1 tabIndex={-1}>{detail?.run.tag ?? "Rebate run"}</h1>
      {found === undefined ? (
        <Skeleton shape="table" columns={5} rows={8} />
      ) : typeof found === "string" ? (
        <Problem found={found} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        <>
          <RunFacts detail={found} timeZone={org.time_zone} />
          <div className="tabs" role="group" aria-label="Run actions">
            {found.can_approve && (
              <button
                type="button"
                className="primary"
                data-testid="run-approve"
                disabled={busy}
                onClick={() => {
                  const checks = found.checks ?? runTotals(found.lines).checks;
                  if (!confirm(`Approve ${count(checks, "check")} for ${dollars(found.run.total)}? After the approval, the amounts stay as they are.`)) return;
                  void act((token) => api.approvePayoutRun(token, id), "Saved: the run is approved.", "The approval");
                }}
              >
                Approve
              </button>
            )}
            {found.can_export && (
              <>
                <button type="button" data-testid="run-export-quickbooks" disabled={busy} onClick={() => void exportRun("quickbooks")}>
                  File for QuickBooks
                </button>
                <button type="button" data-testid="run-export-checks" disabled={busy} onClick={() => void exportRun("checks")}>
                  File for the checks
                </button>
              </>
            )}
          </div>
          {found.run.status === "draft" && found.paid_by_other && (
            <p className="error" data-testid="run-paid-by-other">
              {found.paid_by_other}
            </p>
          )}
          {found.run.status === "draft" && !found.can_approve && !found.paid_by_other && (
            <p className="sub" data-testid="run-needs-approval">
              An administrator, an operations manager or an accountant who did not make the run approves it before the export.
            </p>
          )}
          <Status saved={saved} auth={auth} testId="run-action-status" />
          <h2 id="run-lines">Lines</h2>
          {found.lines.length === 0 ? (
            <p data-testid="lines-none">No account with a contract has a pickup in this period.</p>
          ) : (
            <>
              <SortSelect id="run-lines-select" columns={PAYOUT_LINE_COLUMNS} sort={sort} onSort={setSort} fallback="Held first" />
              <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="run-lines">
                <table className="list" data-testid="line-table" data-cols="run-lines-select">
                  <thead>
                    <tr>
                      {PAYOUT_LINE_COLUMNS.map((column) => (
                        <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                      ))}
                      <td className="sticky-end">
                        <span className="visually-hidden">Change</span>
                      </td>
                    </tr>
                  </thead>
                  <tbody>
                    {sortRows(found.lines, PAYOUT_LINE_COLUMNS, sort).map((line) => (
                      <LineRow
                        key={line.id}
                        line={line}
                        detail={found}
                        base={base}
                        org={org}
                        auth={auth}
                        user={user}
                        onSaved={(next) => {
                          const paidNow = next.run.status === "paid" && found.run.status !== "paid";
                          setFound(next);
                          setSaved({ ok: true, text: paidNow ? `Saved: account ${line.number}. The run is paid.` : `Saved: account ${line.number}.` });
                        }}
                      />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}
    </main>
  );
}

function RunFacts({ detail, timeZone }: { detail: api.PayoutDetail; timeZone: string }) {
  const { run, lines } = detail;
  const totals = runTotals(lines);
  return (
    <dl className="facts" data-testid="run-facts">
      <dt>Status</dt>
      <dd data-testid="run-status-text">{RUN_STATUS_TEXT[run.status]}</dd>
      <dt>Period</dt>
      <dd>
        {run.period} · {periodDays(run)}
      </dd>
      <dt>To pay</dt>
      <dd data-testid="run-total">
        {dollars(run.total)} in {count(detail.checks ?? totals.checks, "check")}
        {totals.paid ? `, ${totals.paid} paid` : ""}
      </dd>
      <dt>Held</dt>
      <dd>{totals.held ? `${count(totals.held, "line")}, ${dollars(totals.heldAmount)}` : "None"}</dd>
      <dt>Minimum check</dt>
      <dd>{dollars(run.minimum)}</dd>
      {run.market_value !== null && (
        <>
          <dt>Market value</dt>
          <dd>{dollars(run.market_value)} per gal</dd>
        </>
      )}
      <dt>Made</dt>
      <dd>
        {run.created_email ? emailName(run.created_email) : "–"}, {dayText(callDay(run.created_at, timeZone), true)}
      </dd>
      {run.approved_email && (
        <>
          <dt>Approved</dt>
          <dd>
            {emailName(run.approved_email)}
            {run.approved_at ? `, ${dayText(run.approved_at.slice(0, 10), true)}` : ""}
          </dd>
        </>
      )}
    </dl>
  );
}

function LineRow({
  line,
  detail,
  base,
  org,
  auth,
  user,
  onSaved,
}: {
  line: api.PayoutLine;
  detail: api.PayoutDetail;
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  onSaved: (next: api.PayoutDetail) => void;
}) {
  const [open, setOpen] = useState(false);
  const can = lineChangeable(detail, line);
  const span = PAYOUT_LINE_COLUMNS.length + 1;
  return (
    <>
      <tr data-testid="line-row" data-account={line.number} data-payment={line.payment_status}>
        <th scope="row">
          <a href={`${base}/accounts/${line.account_id}`} onClick={go} title={line.name}>
            {line.number}
          </a>
        </th>
        <td data-label="Payee" className="wrap" title={line.payee_address}>
          {line.payee_name || "–"}
        </td>
        <td className="num" data-label="Net gal">{line.net_gal.toLocaleString("en-US")}</td>
        <td className="num" data-label="Rebate">
          {rebateText(line.method, line.rate)}
          {line.contract_version !== null && <span className="sub"> · v{line.contract_version}</span>}
        </td>
        <td className="num" data-label="Amount">{dollars(line.amount)}</td>
        <td className="num" data-label="Adjustment">{line.adjustment ? dollars(line.adjustment) : "–"}</td>
        <td className="num" data-label="To pay" title={line.adjustment ? `Amount ${dollars(line.amount)}, adjustment ${dollars(line.adjustment)}` : undefined}>
          {dollars(line.final_amount)}
        </td>
        <td data-label="Payment" className="wrap payment">
          {PAYMENT_TEXT[line.payment_status]}
          {line.paid_on && <span className="sub"> · {dayText(line.paid_on, true)}</span>}
          {line.hand_delivered && <span className="sub"> · by hand</span>}
          {line.carried_from_tag && (
            <span className="sub below" data-testid="line-carried-from">
              Carried from {line.carried_from_tag}
            </span>
          )}
          {line.carried_to_tag && (
            <span className="sub below" data-testid="line-carried-to">
              Carried to {line.carried_to_tag}
            </span>
          )}
        </td>
        <td data-label="Hold reason" className="wrap" title={line.hold_reason || undefined}>
          <span className="clip reason">{line.hold_reason || "–"}</span>
        </td>
        <td className="sticky-end">
          {can && (
            <button type="button" data-testid="line-change" aria-expanded={open} onClick={() => setOpen(!open)}>
              {detail.can_edit_lines ? "Change" : "Payment"}
            </button>
          )}
        </td>
      </tr>
      {open && (
        <tr className="detail-row">
          <td colSpan={span}>
            {detail.can_edit_lines ? (
              <DraftLineForm line={line} detail={detail} auth={auth} user={user} onSaved={onSaved} onDone={() => setOpen(false)} />
            ) : (
              <PaymentForm line={line} detail={detail} org={org} auth={auth} user={user} onSaved={onSaved} onDone={() => setOpen(false)} />
            )}
          </td>
        </tr>
      )}
    </>
  );
}

interface LineFormProps {
  line: api.PayoutLine;
  detail: api.PayoutDetail;
  auth: Auth;
  user: User;
  onSaved: (next: api.PayoutDetail) => void;
  onDone: () => void;
}

function DraftLineForm({ line, detail, auth, user, onSaved, onDone }: LineFormProps) {
  const [adjustment, setAdjustment] = useState(line.adjustment ? String(line.adjustment) : "");
  const [hold, setHold] = useState(line.hold_reason);
  const [saved, setSaved] = useState<Saved | null>(null);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const value = adjustmentOf(adjustment, line.amount);
    if (typeof value === "string") return setSaved({ ok: false, text: value });
    if (hold.trim().length > HOLD_MAX) return setSaved({ ok: false, text: `The hold reason can have ${HOLD_MAX} characters at most.` });
    try {
      onSaved(await api.changePayoutLine(await user.getIdToken(), detail.run.id, line.id, { adjustment: value, hold_reason: hold.trim() }));
      onDone();
    } catch (e) {
      setSaved(saveFailure(e, "The line"));
    }
  };
  return (
    <form onSubmit={submit} noValidate className="inline-form" data-testid="line-form">
      <label htmlFor={`adjust-${line.id}`}>Adjustment ($, below zero to take away)</label>
      <input id={`adjust-${line.id}`} data-testid="line-adjustment" inputMode="decimal" value={adjustment} onChange={(e) => setAdjustment(e.target.value)} />
      <label htmlFor={`hold-${line.id}`}>{line.rule_hold ? "Hold reason (the run rules set it)" : "Hold reason (empty to pay the line)"}</label>
      <input id={`hold-${line.id}`} data-testid="line-hold" value={hold} disabled={line.rule_hold} onChange={(e) => setHold(e.target.value)} />
      {line.rule_hold && <p className="sub">Correct the cause, for example the contract or the payee, then make a new run.</p>}
      <button type="submit" className="primary">
        Save
      </button>
      <button type="button" onClick={onDone}>
        Cancel
      </button>
      <Status saved={saved} auth={auth} testId="line-status" />
    </form>
  );
}

function PaymentForm({ line, detail, org, auth, user, onSaved, onDone }: LineFormProps & { org: api.Bootstrap }) {
  const [payment, setPayment] = useState<api.PayoutLine["payment_status"]>(line.payment_status);
  const [paidOn, setPaidOn] = useState(line.paid_on ?? "");
  const [byHand, setByHand] = useState(line.hand_delivered);
  const [reason, setReason] = useState(line.hold_reason);
  const [saved, setSaved] = useState<Saved | null>(null);
  const today = todayIn(org.time_zone);
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const change: api.LineChange = { payment_status: payment, hand_delivered: byHand };
    if (payment === "paid" && paidOn) change.paid_on = paidOn;
    if (payment === "held") {
      if (reason.trim().length > HOLD_MAX) return setSaved({ ok: false, text: `The hold reason can have ${HOLD_MAX} characters at most.` });
      change.hold_reason = reason.trim();
    }
    try {
      onSaved(await api.changePayoutLine(await user.getIdToken(), detail.run.id, line.id, change));
      onDone();
    } catch (e) {
      setSaved(saveFailure(e, "The payment"));
    }
  };
  return (
    <form onSubmit={submit} noValidate className="inline-form" data-testid="payment-form">
      <label htmlFor={`payment-${line.id}`}>Payment</label>
      <select id={`payment-${line.id}`} data-testid="line-payment" value={payment} onChange={(e) => setPayment(e.target.value as api.PayoutLine["payment_status"])}>
        {Object.entries(PAYMENT_TEXT).map(([value, label]) => (
          <option key={value} value={value}>
            {label}
          </option>
        ))}
      </select>
      {payment === "paid" && (
        <>
          <label htmlFor={`paid-${line.id}`}>Paid on (empty for today)</label>
          <input id={`paid-${line.id}`} type="date" max={today} value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
        </>
      )}
      {payment === "held" && (
        <>
          <label htmlFor={`reason-${line.id}`}>Hold reason</label>
          <input id={`reason-${line.id}`} value={reason} onChange={(e) => setReason(e.target.value)} />
        </>
      )}
      <label className="check">
        <input type="checkbox" checked={byHand} onChange={(e) => setByHand(e.target.checked)} /> A salesperson delivers the check by hand
      </label>
      <button type="submit" className="primary">
        Save
      </button>
      <button type="button" onClick={onDone}>
        Cancel
      </button>
      <Status saved={saved} auth={auth} testId="payment-status" />
    </form>
  );
}

/** F-PAY-8: the payout history of the account, for the roles with the contract grant. */
export function PayoutHistory({ account, base }: { account: api.AccountDetail; base: string }) {
  const [sort, setSort] = useSort("psort", ACCOUNT_PAYOUT_COLUMNS);
  const rows = account.payouts;
  if (!rows) return null;
  return (
    <section aria-labelledby="account-payouts" data-testid="payout-section">
      <h2 id="account-payouts">Rebate payments</h2>
      {rows.length === 0 ? (
        <p data-testid="payouts-none">No approved rebate run has this account yet.</p>
      ) : (
        <>
          <SortSelect id="payouts-select" columns={ACCOUNT_PAYOUT_COLUMNS} sort={sort} onSort={setSort} fallback="Newest first" />
          <div className="table-scroll" tabIndex={0} role="region" aria-labelledby="account-payouts">
            <table className="list" data-testid="payout-table" data-cols="payouts-select">
              <thead>
                <tr>
                  {ACCOUNT_PAYOUT_COLUMNS.map((column) => (
                    <SortTh key={column.key} column={column} sort={sort} onSort={setSort} />
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortRows(rows, ACCOUNT_PAYOUT_COLUMNS, sort).map((row) => (
                  <tr key={row.id} data-testid="payout-row">
                    <th scope="row">
                      <a href={`${base}/rebates/${row.run_id}`} onClick={go}>
                        {row.tag}
                      </a>
                    </th>
                    <td data-label="Period">
                      {dayText(row.starts_on, true)} to {dayText(row.ends_on, true)}
                    </td>
                    <td className="num" data-label="Net gal">{row.net_gal.toLocaleString("en-US")}</td>
                    <td className="num" data-label="Amount" title={rebateText(row.method, row.rate)}>
                      {dollars(row.final_amount)}
                    </td>
                    <td data-label="Payment" className="wrap payment">
                      {PAYMENT_TEXT[row.payment_status]}
                      {row.hand_delivered && <span className="sub"> · by hand</span>}
                      {row.payment_status === "held" && row.hold_reason && (
                        <span className="sub below" data-testid="payout-hold-reason">
                          {row.hold_reason}
                        </span>
                      )}
                    </td>
                    <td data-label="Paid on">{row.paid_on ? dayText(row.paid_on, true) : "–"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
}
